import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  leadService, quoteService, driveService, featureFlagsService,
  integrationService, adminService, notificationService,
} from '../services/api';
import {
  AlertTriangle, Users, FileText, Calculator, HardDrive, Activity, ShieldCheck,
  ArrowRight, Loader2, TrendingUp, Clock, Bell, RefreshCw, Search, CheckCircle2,
  FolderOpen, GitBranch, Gauge,
} from 'lucide-react';

const settled = (r, fb = null) => (r.status === 'fulfilled' ? r.value : fb);

// Compact, clickable KPI tile (mobile-first).
const Tile = ({ testid, icon: Icon, label, value, sub, tone = 'slate', onClick }) => {
  const tones = {
    slate: 'border-slate-200 hover:border-slate-300',
    rose: 'border-rose-200 bg-rose-50/60 hover:border-rose-300',
    amber: 'border-amber-200 bg-amber-50/60 hover:border-amber-300',
    blue: 'border-blue-200 bg-blue-50/50 hover:border-blue-300',
    green: 'border-green-200 bg-green-50/50 hover:border-green-300',
    indigo: 'border-indigo-200 bg-indigo-50/50 hover:border-indigo-300',
  };
  const iconTone = {
    slate: 'text-slate-500', rose: 'text-rose-600', amber: 'text-amber-600',
    blue: 'text-blue-600', green: 'text-green-600', indigo: 'text-indigo-600',
  };
  return (
    <button
      type="button" onClick={onClick} disabled={!onClick} data-testid={testid}
      className={`group text-left rounded-xl border ${tones[tone]} p-3 transition-colors ${onClick ? 'cursor-pointer' : 'cursor-default'} flex flex-col gap-1 min-h-[84px]`}
    >
      <div className="flex items-center justify-between">
        <Icon className={`w-4 h-4 ${iconTone[tone]}`} />
        {onClick && <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />}
      </div>
      <span className="text-2xl font-bold text-gray-900 leading-none">{value}</span>
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {sub && <span className="text-[11px] text-gray-400">{sub}</span>}
    </button>
  );
};

const Section = ({ title, icon: Icon, children, testid, action }) => (
  <section data-testid={testid} className="space-y-3">
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />} {title}
      </h2>
      {action}
    </div>
    {children}
  </section>
);

const FounderCommandCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const load = async () => {
    setLoading(true);
    const [leads, quotesRes, driveStats, schedule, digest, flags, integ, atRisk, failed, expiring, ownerDay] = await Promise.allSettled([
      leadService.getStats(),
      quoteService.listQuotes({ limit: 200 }),
      driveService.getStats(),
      driveService.getSchedule(),
      driveService.getDigest(),
      featureFlagsService.getEnabledFeatures(),
      integrationService.isEnabled(),
      adminService.getDriversAtRisk(30),
      notificationService.getDeliveryHistory('failed', 10),
      driveService.getExpiringDocuments(60),
      integrationService.getOwnerDay(),
    ]);
    setData({
      leads: settled(leads, {}),
      quotes: settled(quotesRes, { items: [], total: 0 }),
      driveStats: settled(driveStats, {}),
      schedule: settled(schedule, {}),
      digest: settled(digest, {}),
      flags: settled(flags, {}),
      integ: settled(integ, false),
      atRisk: settled(atRisk, {}),
      failed: settled(failed, null),
      expiring: settled(expiring, null),
      ownerDay: settled(ownerDay, null),
    });
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) {
    return <div className="flex justify-center py-24" data-testid="fcc-loading"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  const leads = data.leads || {};
  const leadTotal = leads.total || 0;
  const byStatus = Object.fromEntries((leads.by_status || []).map((s) => [s.key, s.count]));
  const unanswered = (byStatus['quote_opportunity_no_response'] || 0) + (byStatus['new'] || 0);
  const won = byStatus['won'] || 0;

  const quotes = (data.quotes?.items) || [];
  const quoteTotal = data.quotes?.total || 0;
  const qCount = (st) => quotes.filter((q) => q.status === st).length;
  const draftQuotes = qCount('draft');
  const sentQuotes = qCount('sent') + qCount('viewed');
  const acceptedQuotes = qCount('accepted');
  const awaitingAction = draftQuotes + sentQuotes;

  const driveStats = data.driveStats || {};
  const driveTotal = driveStats.total || 0;
  const digest = data.digest || {};
  const highAlerts = digest.high_priority_unresolved || 0;
  const actionable = digest.actionable_count || 0;
  const schedule = data.schedule || {};
  const lastScan = schedule.last_successful_scheduled_scan?.started_at || schedule.last_successful_scheduled_scan?.completed_at;
  const flagsOn = Array.isArray(data.flags) ? data.flags.length : (data.flags?.enabled_features?.length || Object.keys(data.flags || {}).filter((k) => data.flags[k]).length || 5);
  const atRiskDrivers = data.atRisk?.drivers_at_risk || data.atRisk?.drivers || [];
  const failedResp = data.failed;
  const failedCount = failedResp?.stats?.failed ?? (Array.isArray(failedResp?.deliveries) ? failedResp.deliveries.length : (failedResp?.items?.length ?? null));
  const expiringResp = data.expiring;
  const expiringCount = expiringResp?.count ?? null;

  const recentQuotes = [...quotes].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 5);
  const driveChanges = (digest.categories || []).flatMap((c) => (c.items || []).map((it) => ({ ...it, category: c.category }))).slice(0, 5);

  const go = (path) => () => navigate(path);
  // Route each Owner-Day attention item to its correct operational context instead of
  // dumping everything on the Integration Console (Founder-reported routing defect).
  const OWNER_DAY_ROUTES = {
    accepted_quotes_awaiting_jobs: '/admin/jobs',
    jobs_awaiting_completion: '/admin/jobs',
    open_invoices: '/admin/accounting',
    unverified_payments: '/admin/accounting',
    drivers_needing_verification: '/admin/dispatch',
    expiring_compliance: '/admin/documents',
  };
  const ownerDayRoute = (key) => OWNER_DAY_ROUTES[key] || '/admin/dashboard';
  const fmtDate = (d) => { try { return new Date(d).toLocaleString(); } catch { return d || '—'; } };

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-16" data-testid="founder-command-center">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-blue-600">Command Center</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Welcome, {user?.first_name || 'Founder'}</h1>
          <p className="text-gray-600 mt-1 text-sm">Your BlueLock operating system at a glance — attention, business, activity and system health.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={go('/admin/customer-360')} data-testid="fcc-search-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"><Search className="w-4 h-4" /> Customer 360</button>
          <button onClick={load} data-testid="fcc-refresh-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>
      </div>

      {/* Owner Day View — concise daily operating layer over canonical sources */}
      {(() => {
        const od = data.ownerDay;
        const items = od?.attention_items || [];
        const toneFor = (p) => (p === 'action' ? 'amber' : p === 'attention' ? 'rose' : 'slate');
        return (
          <Section title="Owner Day View — What Needs My Attention Today" icon={AlertTriangle} testid="fcc-owner-day">
            {od == null ? (
              <p className="text-xs text-gray-400" data-testid="fcc-owner-day-unavailable">Source unavailable.</p>
            ) : items.length === 0 ? (
              <p className="text-xs text-green-700 flex items-center gap-1.5" data-testid="fcc-owner-day-clear"><CheckCircle2 className="w-4 h-4" /> Nothing needs your attention today.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="fcc-owner-day-items">
                {items.map((it) => (
                  <Tile key={it.key} testid={`fcc-owner-day-${it.key}`} icon={AlertTriangle} tone={toneFor(it.priority)} value={it.count} label={it.message} sub={it.priority === 'action' ? 'action' : 'attention'} onClick={go(ownerDayRoute(it.key))} />
                ))}
              </div>
            )}
          </Section>
        );
      })()}

      {/* A. Attention Needed */}
      <Section title="Attention Needed" icon={AlertTriangle} testid="fcc-attention">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Tile testid="fcc-attn-alerts" icon={Bell} tone={highAlerts > 0 ? 'rose' : 'slate'} value={highAlerts} label="High-priority Drive alerts" sub={highAlerts > 0 ? `${actionable} actionable` : 'none unresolved'} onClick={go('/admin/integration')} />
          <Tile testid="fcc-attn-failed" icon={Bell} tone={failedCount > 0 ? 'rose' : 'slate'} value={failedCount == null ? '—' : failedCount} label="Failed notifications" sub={failedCount == null ? 'source unavailable' : 'delivery failures'} onClick={failedCount == null ? undefined : go('/admin/communications')} />
          <Tile testid="fcc-attn-expiring" icon={FileText} tone={expiringCount > 0 ? 'amber' : 'slate'} value={expiringCount == null ? '—' : expiringCount} label="Expiring documents" sub={expiringCount == null ? 'source unavailable' : `${expiringResp?.window_days || 60}-day window`} onClick={expiringCount == null ? undefined : go('/admin/documents')} />
          <Tile testid="fcc-attn-quotes" icon={Calculator} tone={awaitingAction > 0 ? 'amber' : 'slate'} value={awaitingAction} label="Quotes awaiting action" sub={`${draftQuotes} draft · ${sentQuotes} sent`} onClick={go('/admin/quote-builder')} />
          <Tile testid="fcc-attn-leads" icon={Users} tone={unanswered > 0 ? 'amber' : 'slate'} value={unanswered} label="Unanswered leads" onClick={go('/admin/leads')} />
          <Tile testid="fcc-attn-drivers" icon={ShieldCheck} tone={atRiskDrivers.length > 0 ? 'amber' : 'slate'} value={atRiskDrivers.length} label="At-risk drivers" sub={atRiskDrivers.length > 0 ? 'expiring compliance' : 'none'} onClick={atRiskDrivers.length > 0 ? go('/admin/attention') : undefined} />
        </div>
        {(failedCount === 0 && expiringCount === 0 && highAlerts === 0 && awaitingAction === 0 && unanswered === 0 && atRiskDrivers.length === 0) && (
          <p className="text-xs text-green-700 flex items-center gap-1.5" data-testid="fcc-attn-clear"><CheckCircle2 className="w-4 h-4" /> No critical items requiring attention.</p>
        )}
      </Section>

      {/* B. Business Snapshot */}
      <Section title="Business Snapshot" icon={TrendingUp} testid="fcc-business">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile testid="fcc-biz-customers" icon={Users} tone="blue" value={leadTotal} label="Customers & leads" onClick={go('/admin/leads')} />
          <Tile testid="fcc-biz-openquotes" icon={Calculator} tone="blue" value={awaitingAction} label="Open quotes" sub={`${quoteTotal} total`} onClick={go('/admin/quote-builder')} />
          <Tile testid="fcc-biz-accepted" icon={CheckCircle2} tone="green" value={acceptedQuotes} label="Accepted quotes" onClick={go('/admin/quote-builder')} />
          <Tile testid="fcc-biz-won" icon={TrendingUp} tone="green" value={won} label="Won leads" onClick={go('/admin/leads')} />
        </div>
        <p className="text-[11px] text-gray-400">Revenue and invoicing metrics are not shown — no governed financial source is active yet.</p>
      </Section>

      {/* C. Recent Activity */}
      <Section title="Recent Activity" icon={Clock} testid="fcc-recent">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-xl border border-gray-200 p-3" data-testid="fcc-recent-quotes">
            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><Calculator className="w-3.5 h-3.5 text-blue-500" /> Recent quotes</p>
            {recentQuotes.length === 0 ? <p className="text-xs text-gray-400">No quotes yet.</p> : (
              <ul className="divide-y divide-gray-100">
                {recentQuotes.map((q) => (
                  <li key={q.id}>
                    <button onClick={go('/admin/quote-builder')} className="w-full text-left py-1.5 flex items-center justify-between text-xs hover:bg-gray-50 rounded px-1">
                      <span className="truncate"><span className="capitalize font-medium text-gray-800">{q.status}</span> · {q.service_code || 'quote'}</span>
                      <span className="text-gray-500">${(q.total || 0).toFixed(0)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-xl border border-gray-200 p-3" data-testid="fcc-recent-drive">
            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><HardDrive className="w-3.5 h-3.5 text-indigo-500" /> Recent Drive changes</p>
            {driveChanges.length === 0 ? <p className="text-xs text-gray-400">No recent change events.</p> : (
              <ul className="divide-y divide-gray-100">
                {driveChanges.map((c, i) => (
                  <li key={i}>
                    <button onClick={go('/admin/integration')} className="w-full text-left py-1.5 flex items-center justify-between text-xs hover:bg-gray-50 rounded px-1">
                      <span className="truncate capitalize">{(c.category || '').replace(/_/g, ' ')}</span>
                      <span className="text-gray-400 truncate max-w-[45%]">{(c.source_path || '').split('/').pop()}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Section>

      {/* D. System / Intelligence */}
      <Section title="System & Intelligence" icon={Activity} testid="fcc-system">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile testid="fcc-sys-drive" icon={FolderOpen} tone="indigo" value={driveTotal} label="Drive documents indexed" sub={data.integ ? 'Read-only · connected' : 'integration'} onClick={go('/admin/integration')} />
          <Tile testid="fcc-sys-scan" icon={RefreshCw} tone="slate" value={schedule.status === 'enabled' ? 'On' : 'Off'} label="Daily Drive scan" sub={lastScan ? `Last: ${fmtDate(lastScan).split(',')[0]}` : 'no scan yet'} onClick={go('/admin/integration')} />
          <Tile testid="fcc-sys-flags" icon={Gauge} tone="slate" value={`${flagsOn} / 68`} label="Feature flags ON" onClick={go('/admin/activation-status')} />
          <Tile testid="fcc-sys-relationship" icon={GitBranch} tone="indigo" value={data.integ ? 'Active' : 'Off'} label="Relationship & Search" onClick={go('/admin/integration')} />
        </div>
      </Section>
    </div>
  );
};

export default FounderCommandCenter;
